import React from "react";
import { Card, Form, Input, Button } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import RichTextEditor, { isRichTextEmpty } from "components/shared-components/RichTextEditor";
import { postEduApi } from "./educationApi";

// Content sections of the education screens (Monthly, Toolbox, Memo, SOP...):
// each a heading plus rich text (the same CKEditor the old Education web used),
// saved as the "new_content" HTML the app shows in its web view: one
// <tr class="new_cnt"> per section, on one line, attributes in single quotes.

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const IMAGE_MAX_MB = 5;

// an image picked in the editor goes to storage; the editor shows its URL
const uploadEditorImage = (file) => {
  if (!IMAGE_TYPES.includes(file.type)) return Promise.reject(new Error("Please select a JPG, PNG, GIF or WEBP image."));
  if (file.size > IMAGE_MAX_MB * 1024 * 1024) return Promise.reject(new Error(`The image must be ${IMAGE_MAX_MB} MB or smaller.`));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const { error, result } = await postEduApi("/upload_image", { file: reader.result });
      if (!error && result?.success && result.result) resolve(result.result);
      else reject(new Error("The image could not be uploaded. Please try again."));
    };
    reader.onerror = () => reject(new Error("The image could not be read."));
    reader.readAsDataURL(file);
  });
};

const escapeText = (s) =>
  String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

export const buildNewContent = (sections) => {
  const table = document.createElement("table");
  table.innerHTML = (sections || []).map((s) => `<tr class="new_cnt"><td colspan="4"><b>${escapeText(s.heading)}</b>${s.content || ""}</td></tr>`).join("");
  // images fit the phone screen and keep their shape
  table.querySelectorAll("img").forEach((img) => {
    img.removeAttribute("width");
    img.removeAttribute("height");
    img.style.maxWidth = "100%";
    img.style.height = "auto";
  });
  const html = table.querySelector("tbody") ? table.querySelector("tbody").innerHTML : "";
  // one line, attribute values in single quotes (as the old screens saved it)
  return html.replace(/\n/g, "").replace(/="([^"]*)"/g, (_, v) => `='${v.replace(/'/g, "&#39;")}'`);
};

// The reverse of buildNewContent, to edit saved content: one section per row
// (its bold heading, then the rest as the content). Content saved some other
// way becomes one section headed `fallbackHeading`.
export const parseNewContent = (html, fallbackHeading = "") => {
  const table = document.createElement("table");
  table.innerHTML = `<tbody>${html || ""}</tbody>`;
  const sections = Array.from(table.querySelectorAll("tbody > tr")).map((tr) => {
    const cell = tr.querySelector("td") || tr;
    const bold = cell.firstElementChild && cell.firstElementChild.tagName === "B" ? cell.firstElementChild : null;
    const heading = bold ? bold.textContent.trim() : "";
    if (bold) bold.remove();
    return { heading, content: cell.innerHTML };
  });
  if (sections.length && sections.some((s) => s.heading || s.content)) return sections;
  return [{ heading: fallbackHeading, content: html || "" }];
};

const contentRules = [
  {
    validator: (_, value) => (isRichTextEmpty(value) ? Promise.reject(new Error("Please enter the content.")) : Promise.resolve()),
  },
];

export const ContentSectionsFormList = () => (
  <Form.List name="sections" initialValue={[{}]}>
    {(fields, { add, remove }) => (
      <>
        <div style={{ marginBottom: 8, fontSize: 13, color: "#5B6472" }}>Content Sections</div>
        {fields.map(({ key, name, ...rest }, index) => (
          <Card key={key} size="small" style={{ marginBottom: 8 }}>
            <Form.Item
              {...rest}
              name={[name, "heading"]}
              label={`Heading ${index + 1}`}
              rules={[{ required: true, whitespace: true, message: "Please enter the heading." }]}
            >
              <Input placeholder="Enter heading" maxLength={200} />
            </Form.Item>
            <Form.Item
              {...rest}
              name={[name, "content"]}
              label="Content"
              required
              rules={contentRules}
              extra={`Images: JPG, PNG, GIF or WEBP · Max ${IMAGE_MAX_MB} MB`}
            >
              <RichTextEditor onUploadImage={uploadEditorImage} />
            </Form.Item>
            {fields.length > 1 && (
              <Button danger size="small" icon={<MinusCircleOutlined />} onClick={() => remove(name)}>
                Remove this section
              </Button>
            )}
          </Card>
        ))}
        <Button block icon={<PlusOutlined />} onClick={() => add({})}>
          Add Section
        </Button>
      </>
    )}
  </Form.List>
);
